import type { BpmnElement, BpmnProcessModel } from '../bpmn/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { KeyValue } from '../components/KeyValue';
import { lookupTask, pythonMethodsFor, type ManifestView, type TaskBinding } from '../microapp/manifest';

export interface ElementDetailProps {
  process: BpmnProcessModel;
  elementId: string;
  manifest: ManifestView;
  /** DMN Taskから判定表へ移動する。 */
  onOpenDecision(decisionKey: string): void;
  /** Call Activityから呼出先プロセスへ移動する。 */
  onOpenProcess(processKey: string): void;
}

/**
 * BPMN要素とその実装の対応を出す。
 * Task名（BPMNのname）・activityId・taskKey・Pythonクラス名は別の文字列なので、
 * ラベルを付けて並べ、混同できない形にする。
 */
export function ElementDetail({
  process,
  elementId,
  manifest,
  onOpenDecision,
  onOpenProcess,
}: ElementDetailProps) {
  const element = process.elements[elementId];
  if (!element) {
    return (
      <p className="hint">
        要素 <code>{elementId}</code> はこのプロセスの定義に見つかりません。
      </p>
    );
  }

  const found = lookupTask(manifest, process.processKey, elementId);

  return (
    <div className="element-detail">
      <KeyValue
        items={[
          { label: 'Task名', value: element.name || '（名前なし）' },
          { label: 'activityId', value: <code>{element.id}</code> },
          { label: 'BPMN要素', value: <code>{element.type}</code> },
        ]}
      />

      {element.documentation && <p className="element-doc">{element.documentation}</p>}

      <FlowableDetail element={element} onOpenDecision={onOpenDecision} onOpenProcess={onOpenProcess} />

      {found === null ? (
        <p className="hint">
          この要素にはmanifestのbindingがありません（Python実装と対応しない要素です）。
        </p>
      ) : (
        <section className="detail-section">
          <h4>実装の対応</h4>
          <KeyValue
            items={[
              { label: 'taskKey', value: <code>{found.binding.taskKey}</code> },
              {
                label: '種別',
                value: found.declaration ? (
                  <Badge tone="neutral">
                    {found.declaration.kind === 'human' ? 'HumanTask' : 'ServiceTask'}
                  </Badge>
                ) : (
                  '不明'
                ),
              },
              {
                label: 'Pythonクラス',
                value: found.declaration ? <code>{found.declaration.taskKey}</code> : '未定義',
              },
              {
                label: 'メソッド',
                value: found.declaration
                  ? pythonMethodsFor(found.declaration)
                      .map((method) => `${method}()`)
                      .join(' / ')
                  : '未定義',
              },
            ]}
          />

          {found.declaration && (
            <KeyValue
              items={[
                { label: 'inputSchema', value: <code>{found.declaration.inputSchema ?? '—'}</code> },
                {
                  label: 'outputSchema',
                  value: <code>{found.declaration.outputSchema ?? '—'}</code>,
                },
                ...(found.declaration.kind === 'human'
                  ? [
                      {
                        label: 'answerSchema',
                        value: <code>{found.declaration.answerSchema ?? '—'}</code>,
                      },
                    ]
                  : []),
                ...(found.declaration.conversationProviders.length > 0
                  ? [
                      {
                        label: '会話',
                        value: found.declaration.conversationProviders.join(' / '),
                      },
                    ]
                  : []),
              ]}
            />
          )}

          <MappingDetail binding={found.binding} />
        </section>
      )}
    </div>
  );
}

function FlowableDetail({
  element,
  onOpenDecision,
  onOpenProcess,
}: {
  element: BpmnElement;
  onOpenDecision(decisionKey: string): void;
  onOpenProcess(processKey: string): void;
}) {
  const rows = [];
  if (element.flowableType) {
    rows.push({ label: 'flowable:type', value: <code>{element.flowableType}</code> });
  }
  if (element.topic) {
    // topicは基盤が生成・検証する値。業務実装はmanifestのtaskKeyから参照する。
    rows.push({ label: 'topic', value: <code>{element.topic}</code> });
  }
  if (element.messageName) {
    rows.push({ label: 'message', value: <code>{element.messageName}</code> });
  }
  if (element.timer) {
    rows.push({
      label: `Timer（${element.timer.kind}）`,
      value: <code>{element.timer.value}</code>,
    });
  }
  if (element.boundary) {
    rows.push({
      label: '境界イベント',
      value: `${element.boundary.attachedToRef} に付随（${
        element.boundary.cancelActivity ? '割込み' : '非割込み'
      }）`,
    });
  }
  if (element.terminate) {
    rows.push({ label: '終了イベント', value: 'terminate（並列の枝と子の Call Activity も終える）' });
  }

  return (
    <section className="detail-section">
      {rows.length > 0 && <KeyValue items={rows} />}

      {element.decisionKey && (
        <p className="row">
          <span className="field-label">判定表</span>
          <Button variant="text" onClick={() => onOpenDecision(element.decisionKey!)}>
            {element.decisionKey} を開く
          </Button>
        </p>
      )}

      {element.calledElement && (
        <p className="row">
          <span className="field-label">Call Activity</span>
          <Button variant="text" onClick={() => onOpenProcess(element.calledElement!)}>
            {element.calledElement} を開く
          </Button>
        </p>
      )}

      {(element.workerIn.length > 0 || element.workerOut.length > 0) && (
        <div className="worker-params">
          <h4>external worker パラメータ</h4>
          <ul>
            {element.workerIn.map((param, index) => (
              <li key={`in-${index}`}>
                in: <code>{param.source ?? param.sourceExpression}</code> →{' '}
                <code>{param.target ?? param.targetExpression}</code>
              </li>
            ))}
            {element.workerOut.map((param, index) => (
              <li key={`out-${index}`}>
                out: <code>{param.source ?? param.sourceExpression}</code> →{' '}
                <code>{param.target ?? param.targetExpression}</code>
              </li>
            ))}
          </ul>
        </div>
      )}

      {element.multiInstance && (
        <div className="multi-instance">
          <h4>multi-instance</h4>
          <KeyValue
            items={[
              {
                label: '実行',
                value: element.multiInstance.isSequential ? '逐次' : '並列',
              },
              { label: 'collection', value: <code>{element.multiInstance.collection ?? '—'}</code> },
              {
                label: 'elementVariable',
                value: <code>{element.multiInstance.elementVariable ?? '—'}</code>,
              },
              ...(element.multiInstance.aggregationTarget
                ? [
                    {
                      label: '集約先',
                      value: <code>{element.multiInstance.aggregationTarget}</code>,
                    },
                  ]
                : []),
            ]}
          />
          {element.multiInstance.aggregationVariables.length > 0 && (
            <ul>
              {element.multiInstance.aggregationVariables.map((variable) => (
                <li key={variable.target}>
                  <code>{variable.source ?? variable.sourceExpression}</code> →{' '}
                  <code>{variable.target}</code>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function MappingDetail({ binding }: { binding: TaskBinding }) {
  return (
    <div className="mapping-detail">
      <h4>入出力のmapping</h4>
      {binding.mapping.style === 'single' && (
        <KeyValue
          items={[
            { label: 'input', value: <code>{binding.mapping.input}</code> },
            { label: 'outputVariable', value: <code>{binding.mapping.outputVariable}</code> },
          ]}
        />
      )}
      {binding.mapping.style === 'mapped' && (
        <>
          <MapTable title="inputMap" entries={binding.mapping.inputMap} />
          <MapTable title="outputMap" entries={binding.mapping.outputMap} />
        </>
      )}
      {binding.mapping.style === 'unknown' && (
        <p className="hint">
          input/outputVariable も inputMap/outputMap も読めませんでした。manifestを確認してください。
        </p>
      )}

      <h4>公開projection</h4>
      {binding.publicInputMap === null && binding.publicOutputMap === null ? (
        <p className="hint">
          指定がないため、Requestのinput/outputは全体が公開されます。添付を含むTaskでは
          publicInputMap / publicOutputMap を指定してください。
        </p>
      ) : (
        <>
          {binding.publicInputMap && <MapTable title="publicInputMap" entries={binding.publicInputMap} />}
          {binding.publicOutputMap && (
            <MapTable title="publicOutputMap" entries={binding.publicOutputMap} />
          )}
        </>
      )}
    </div>
  );
}

function MapTable({ title, entries }: { title: string; entries: Record<string, string> }) {
  const rows = Object.entries(entries);
  return (
    <div className="map-table">
      <span className="field-label">{title}</span>
      {rows.length === 0 ? (
        <span className="hint">空（空objectを公開します）</span>
      ) : (
        <ul>
          {rows.map(([key, path]) => (
            <li key={key}>
              <code>{key}</code> ← <code>{path}</code>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
